import { Box, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import type { TableCellProps, TableColumnHeaderProps } from '@chakra-ui/react';
import type { ReactNode, Ref } from 'react';
import { SETTINGS_BORDER_COLOR, SETTINGS_TABLE_GUTTER } from './constants';
import { SETTINGS_ROW_HEIGHT_VAR } from './useDynamicPageSize';

export interface SettingsTableProps {
    /** Header cells, normally SettingsTh elements inside a Tr. */
    head: ReactNode;
    children: ReactNode;
    /** sx from useTableSettings, for density and optional column dividers. */
    tableSettingsSx?: Record<string, any>;
    /**
     * Ref to the scroll box, for useDynamicPageSize to measure how many rows
     * fit in the available height.
     */
    scrollRef?: Ref<HTMLDivElement>;
    /**
     * Stretch the table to the full width of the scroll box and lay columns out
     * from their declared widths (table-layout: fixed) instead of sizing them to
     * their content. Use for matrix-style tables whose columns should share the
     * leftover space evenly.
     */
    fitWidth?: boolean;
    /**
     * With fitWidth, the width below which the table stops shrinking and the
     * scroll box scrolls horizontally instead.
     */
    minTableWidth?: string;
}

/**
 * The app's standard list table: grey header, uppercase column labels, hairline
 * grid, sticky header. Lifted from the dashboard and invoice list tables, which
 * each re-implemented it; new settings tables should use this instead.
 *
 * Pair with SettingsPage variant="table" so it spans sidebar edge to right edge.
 * The table stays full-bleed, but the first and last columns are padded by
 * SETTINGS_TABLE_GUTTER so cell content lines up with the page title on the
 * left and the toolbar buttons on the right. The scroll box is flex-1, so it
 * owns any leftover vertical space and a following footer sits at the bottom.
 * With useDynamicPageSize, the rows stretch to divide that space exactly, so a
 * full page of rows meets the footer with no band of background between them.
 */
const SettingsTable = ({
    head,
    children,
    tableSettingsSx = {},
    scrollRef,
    fitWidth = false,
    minTableWidth,
}: SettingsTableProps) => {
    return (
        <Box
            ref={scrollRef}
            flex={1}
            minH={0}
            overflow="auto"
            borderTopWidth="1px"
            borderColor={SETTINGS_BORDER_COLOR}
            sx={{
                ...tableSettingsSx,
                overscrollBehavior: 'contain',
                // useDynamicPageSize sets the variable to the height that makes
                // a full page of rows land exactly on the footer. Tables
                // without it keep their natural row height.
                '& tbody tr': {
                    height: `var(${SETTINGS_ROW_HEIGHT_VAR}, auto)`,
                },
                '& table': fitWidth
                    ? {
                          width: '100% !important',
                          tableLayout: 'fixed',
                          minWidth: minTableWidth ?? '100%',
                      }
                    : {
                          width: 'max-content !important',
                          minWidth: '100%',
                      },
                '& thead': {
                    zIndex: '3 !important',
                },
                '& th': {
                    ...(tableSettingsSx['& th'] || {}),
                    borderBottomWidth: '0 !important',
                    // Inset shadow draws the header underline; it is a border, not elevation.
                    boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                    bg: 'gray.50',
                },
                // Vertical hairlines between every column, header and body,
                // but never on the table's outer left/right edges.
                '& th:not(:last-of-type)': {
                    borderRightWidth: '1px',
                    borderRightStyle: 'solid',
                    borderRightColor: 'var(--chakra-colors-gray-200)',
                },
                '& td:not(:last-of-type)': {
                    borderRightWidth: '1px',
                    borderRightStyle: 'solid',
                    borderRightColor: 'var(--chakra-colors-gray-100)',
                },
                // Keep the header background full-bleed but inset the columns so the
                // first column aligns with the page title and the last with the
                // right edge of the toolbar.
                '& th:first-of-type, & td:first-of-type': {
                    pl: SETTINGS_TABLE_GUTTER,
                },
                '& th:last-of-type, & td:last-of-type': {
                    pr: SETTINGS_TABLE_GUTTER,
                },
                '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb:hover': { background: '#a0aec0' },
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e0 transparent',
            }}
        >
            <Table variant="simple" size="sm" minW="100%">
                <Thead position="sticky" top={0} zIndex={1}>
                    <Tr bg="gray.50">{head}</Tr>
                </Thead>
                <Tbody>{children}</Tbody>
            </Table>
        </Box>
    );
};

/** Column header with the standard uppercase grey treatment. */
export const SettingsTh = ({ children, ...props }: TableColumnHeaderProps) => (
    <Th
        color="gray.500"
        fontSize="11px"
        fontWeight="semibold"
        letterSpacing="wider"
        textTransform="uppercase"
        borderBottomWidth="1px"
        borderColor={SETTINGS_BORDER_COLOR}
        py={2.5}
        px={3}
        {...props}
    >
        {children}
    </Th>
);

/** Body cell matching SettingsTh spacing and grid lines. */
export const SettingsTd = ({ children, ...props }: TableCellProps) => (
    <Td
        fontSize="sm"
        color="gray.700"
        borderBottomWidth="1px"
        borderColor="gray.100"
        py={2.5}
        px={3}
        {...props}
    >
        {children}
    </Td>
);

export default SettingsTable;
